import {Component, effect, OnDestroy, Signal} from '@angular/core';
import {UserService} from './services/user/user.service';

import {UserWithDefaults} from './models/user';
import {HouseholdWithStores} from './models/household';
import {Store} from './models/store';
import {OAuthService} from 'angular-oauth2-oidc';
import {NavigationEnd, Router} from "@angular/router";
import {ShoppingService} from "./services/shopping/shopping.service";
import {Context} from "./models/context";
import {ContextService} from "./services/context/context-signal.service";
import {AppEventsService} from "./services/app-events/app-events.service";
import {filter, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {AppLogger, AppLoggerService} from "./core/logging";
import {ActiveIconPackService} from "./services/active-icon-pack.service";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnDestroy {

  private readonly log: AppLogger;

  context: Signal<Context> = this.contextService.context;

  // User Data (Household)
  user: UserWithDefaults;
  households: HouseholdWithStores[];

  // Stores
  stores: Store[];

  stockSelectorVisible: boolean = true;
  shoppingLinks: Map<string, string> = new Map<string, string>();

  /** True while the current URL is under /admin – hides the user app shell. */
  isAdminRoute = false;

  /** True while on the /shadcn look-and-feel spike – it builds its own nav/shell
   *  and must never be wrapped in the daisyUI app-shell. */
  isShadcnRoute = false;

  private readonly destroy$ = new Subject<void>();

  constructor(private contextService: ContextService, public router: Router, private oauthService: OAuthService, private shoppingService: ShoppingService, private userService: UserService, private appLogger: AppLoggerService, private appEvents: AppEventsService, private activeIconPackService: ActiveIconPackService) {

    this.log = this.appLogger.createForClass(AppComponent);

    // Load the active icon pack globally once on startup
    this.activeIconPackService.load();

    if (this.oauthService.hasValidAccessToken()) {
      this.loadUser();
    }

    // Detect admin vs user routes on every navigation.
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this.isAdminRoute = e.urlAfterRedirects.startsWith('/admin');
        this.isShadcnRoute = e.urlAfterRedirects.startsWith('/shadcn');

        const visibleRoutes = ['/scan', '/stock', '/some-other-route'];
        this.stockSelectorVisible = visibleRoutes.some(route => this.router.url.startsWith(route));
      });

    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;

      this.loadShoppingLinks(c.household.id);
    });

    this.appEvents.householdChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Reload user defaults and re-publish global context.
        this.loadUser();
      });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUser(): void {
    this.log.debug('Load user profile..');
    this.userService.getUserProfile().subscribe((data) => {
      this.user = data;
      // Check if the user logged in for the first time
      if (data.defaults === undefined) {
        this.userService.clearCache();
        this.loadUser();
        return;
      }

      // Set the default household context globally
      if (data.defaults?.household && data.defaults.household.store) {
        this.log.debug('Setting default household context ' + data.defaults.household.id);
        const ctx = new Context(data.defaults.household, data.defaults.household.store);
        this.contextService.setContext(ctx);
      }
    });
  }

  logout(): void {
    this.oauthService.logOut();
  }

  loadShoppingLinks(householdId: number): void {
    this.shoppingService.restockLinks(householdId).subscribe(
      (data) => {
        this.shoppingLinks = data;
      }
    )
  }

}
